import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Leaf } from 'lucide-react';
import { portalApi } from '../../../api/portal.js';
import { ApiError } from '../../../api/client.js';
import { AdviceCard } from '../../../components/portal/AdviceCard.jsx';

/**
 * Personalized wellness path. On request, asks the server to generate gentle,
 * non-medical advice for the selected pet through the consultant agent grounded
 * in the Historical Memory.
 */
export function WellnessPath() {
  const { selectedPet } = useOutletContext();
  const [state, setState] = useState({ status: 'idle', advice: null });

  if (!selectedPet) return null;

  const generate = async () => {
    setState({ status: 'loading', advice: null });
    try {
      const data = await portalApi.getWellness(selectedPet._id);
      setState({ status: 'ready', advice: data.advice });
    } catch (err) {
      const unavailable = err instanceof ApiError && err.statusCode === 503;
      setState({
        status: 'error',
        advice: null,
        message: unavailable
          ? 'I consigli non sono disponibili in questo momento. Riprova più tardi.'
          : 'Non siamo riusciti a generare i consigli. Riprova.'
      });
    }
  };

  return (
    <div className="space-y-5 pt-2">
      <div>
        <h2 className="text-2xl font-semibold text-portal-ink">Il benessere di {selectedPet.name}</h2>
        <p className="mt-1 text-portal-ink-muted">
          Consigli gentili e personalizzati, pensati a partire dalla storia del tuo animale.
        </p>
      </div>

      {state.status === 'idle' && (
        <div className="rounded-portal bg-portal-card p-8 text-center shadow-portal">
          <Leaf className="mx-auto h-9 w-9 text-portal-sage" strokeWidth={1.5} />
          <p className="mt-3 text-portal-ink-muted">
            Vuoi qualche consiglio su misura per {selectedPet.name}?
          </p>
          <button
            type="button"
            onClick={generate}
            className="mt-5 rounded-full bg-portal-brand px-6 py-2.5 text-sm font-medium text-white hover:bg-portal-brand-hover"
          >
            Genera i consigli
          </button>
        </div>
      )}

      {state.status === 'loading' && <AdviceCard loading />}

      {state.status === 'error' && (
        <div className="rounded-portal bg-portal-card p-6 text-center shadow-portal">
          <p className="text-portal-ink-muted">{state.message}</p>
          <button
            type="button"
            onClick={generate}
            className="mt-4 rounded-full bg-portal-brand px-5 py-2 text-sm font-medium text-white hover:bg-portal-brand-hover"
          >
            Riprova
          </button>
        </div>
      )}

      {state.status === 'ready' && (
        <>
          <AdviceCard advice={state.advice} />
          <div className="text-center">
            <button
              type="button"
              onClick={generate}
              className="text-sm text-portal-ink-muted hover:text-portal-brand"
            >
              Rigenera i consigli
            </button>
          </div>
        </>
      )}
    </div>
  );
}
